// pages/PartnerDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import PartnerSpecialtyDashboard from "./partner/PartnerSpecialtyDashboard";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
// Spécialités supportées (tu peux ajouter: storage, gallery, restauration, etc.)
const SPECIALTIES = {
  insurance: {
    key: "insurance",
    icon: "🛡️",
    titleKey: "partner.insurance.title",
    subtitleKey: "partner.insurance.subtitle",
    typeFilter: ["insurance_quote", "insurance"],
    accent: "amber",
  },
  transport: {
    key: "transport",
    icon: "🚚",
    titleKey: "partner.transport.title",
    subtitleKey: "partner.transport.subtitle",
    typeFilter: ["transport_quote", "transport"],
    accent: "blue",
  },
  valuation_expert: {
    key: "valuation_expert",
    icon: "📈",
    titleKey: "partner.valuation.title",
    subtitleKey: "partner.valuation.subtitle",
    typeFilter: ["valuation", "valuation_quote", "expertise"],
    accent: "gray",
  },
  conservation_expert: {
    key: "conservation_expert",
    icon: "🧪",
    titleKey: "partner.specialties.conservation",
    subtitleKey: "partner.dashboard.subtitleConservation",
    typeFilter: ["conservation", "restoration"],
    accent: "emerald",
  },
  general: {
    key: "general",
    icon: "🤝",
    titleKey: "partner.dashboard.title",
    subtitleKey: "partner.dashboard.subtitle",
    typeFilter: ["general"],
    accent: "gray",
  },
};

// Normalisation tolérante des valeurs
function normalizePartnerType(raw) {
  const v = (raw || "").toLowerCase().trim();

  if (!v) return "general";
  if (["ins", "assurance", "assureur", "insurance", "partnerinsurance"].includes(v)) return "insurance";
  if (["transport", "transporteur", "shipping", "partnertransport"].includes(v)) return "transport";
  if (["expert", "valuation", "valuation_expert", "expert_valuation", "estimation"].includes(v)) return "valuation_expert";
  if (["conservation", "conservation_expert", "conservateur", "restauration", "restoration"].includes(v)) return "conservation_expert";
  if (["notaire", "notary"].includes(v)) return "notaire";

  // si tu stockes déjà des valeurs exactes type "partnerInsurance"
  if (v.includes("insurance")) return "insurance";
  if (v.includes("transport")) return "transport";
  if (v.includes("valuation")) return "valuation_expert";
  if (v.includes("conservation") || v.includes("restoration")) return "conservation_expert";

  return "general";
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [partnerType, setPartnerType] = useState("general");
  const [partnership, setPartnership] = useState(null);
  const [errMsg, setErrMsg] = useState("");

useEffect(() => {
  console.log("[PartnerDashboard] session?", !!session, "profile?", profile);
}, [session, profile]);


  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErrMsg("");

        // 1) user
        const { data: userRes, error: uErr } = await supabase.auth.getUser();
        if (uErr) throw uErr;
        const user = userRes?.user;
        if (!user?.id) throw new Error("No user session");

        // Source de vérité commune avec l'admin Partenaires & accès.
        const { data: partnerRow, error: partnerError } = await supabase
          .from("global_partners")
          .select("id,type,relationship_type,status,access_mode,starts_at,ends_at,renewal_mode,modules_enabled,directory_visible,fee_modele,fee_fixe_ht,fee_pourcentage,tarif_expert_defaut_ht,tarif_client_defaut_ht,contract_document_path")
          .eq("user_id", user.id)
          .maybeSingle();
        if (partnerError) throw partnerError;
        setPartnership(partnerRow || null);

        // 3) fallback: profil (profiles.partner_type — profiles.specialty n'existe pas,
        // un select() la ciblant échouait silencieusement et retombait sur "general")
        let profileType = null;
        const { data: pData, error: pErr } = await supabase
          .from("profiles")
          .select("partner_type,role")
          .eq("id", user.id)
          .maybeSingle();
        if (pErr) console.error("[PartnerDashboard] profile fetch error:", pErr);

        profileType = pData?.partner_type || null;

        const normalized = normalizePartnerType(partnerRow?.type || profileType || "general");
        if (normalized === "notaire") {
          navigate("/notaire", { replace: true });
          return;
        }
        setPartnerType(normalized);

      } catch (e) {
        console.error(e);
        setErrMsg(e?.message || t("partner.dashboard.loadError"));
        setPartnerType("general");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, t]);

  const config = useMemo(() => SPECIALTIES[partnerType] || SPECIALTIES.general, [partnerType]);

  if (loading) {
    return <div className="p-8 text-sm text-gray-500">{t("partner.dashboard.loadingSpace")}</div>;
  }

  if (errMsg) {
    return (
      <div className="p-8">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {errMsg}
        </div>
        <div className="mt-4 text-xs text-gray-500">
          {t("partner.dashboard.configurationHint")}
        </div>
      </div>
    );
  }

  return (
    <PartnerSpecialtyDashboard
      specialtyKey={config.key}
      title={t(config.titleKey)}
      subtitle={t(config.subtitleKey)}
      description={t("partner.dashboard.intro")}
      typeFilter={config.typeFilter}
      accent={config.accent}
      partnership={partnership}
    />
  );
}
